import type { IPin } from '../types/Pin';
import type { IRecording } from '../types';

type SequenceSortable = Pick<IRecording, 'key' | 'sequenceNumber'> & {
  file?: { title?: string };
};

/** Pin types that use their own sequence conventions (not 0-based album order). */
const PIN_TYPES_EXCLUDED_FROM_SEQUENCE_HEALTH = new Set(['wanderPin']);

export interface PinAlbumSequenceHealthIssue {
  code: string;
  severity: 'error' | 'warning';
  message: string;
  pinKey: string;
}

export interface PinAlbumSequenceHealthResult {
  isHealthy: boolean;
  issues: PinAlbumSequenceHealthIssue[];
  canAutoFix: boolean;
  affectedPinCount: number;
}

/**
 * Matches mobile app collection order: lowest sequenceNumber first;
 * recordings without sequenceNumber sort after numbered ones.
 */
export function sortPinRecordingsBySequence<T extends SequenceSortable>(recordings: T[]): T[] {
  return [...recordings].sort((a, b) => {
    const orderA = a.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;

    const titleA = a.file?.title ?? '';
    const titleB = b.file?.title ?? '';
    const titleCompare = titleA.localeCompare(titleB);
    if (titleCompare !== 0) return titleCompare;

    return a.key.localeCompare(b.key);
  });
}

/** Next 0-based sequence for a new recording on a pin (e.g. existing 0,1 → 2). */
export function getNextPinRecordingSequenceNumber(
  recordings: Array<Pick<IRecording, 'sequenceNumber'>>
): number {
  const defined = recordings
    .map((recording) => recording.sequenceNumber)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  if (defined.length === 0) {
    return 0;
  }

  return Math.max(...defined) + 1;
}

function hasDuplicateSequenceNumbers(recordings: SequenceSortable[]): boolean {
  const defined = recordings
    .map((recording) => recording.sequenceNumber)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  return new Set(defined).size !== defined.length;
}

function hasMissingSequenceNumber(recordings: SequenceSortable[]): boolean {
  return recordings.some(
    (recording) =>
      recording.sequenceNumber === undefined ||
      recording.sequenceNumber === null ||
      !Number.isFinite(recording.sequenceNumber)
  );
}

/**
 * Returns Firestore updates to normalise pin album order to 0..n-1 when any
 * recording is missing a sequence or duplicates exist.
 */
export function getPinRecordingSequenceRepairs(
  recordings: SequenceSortable[]
): Array<{ key: string; sequenceNumber: number }> {
  if (recordings.length === 0) {
    return [];
  }

  if (!hasMissingSequenceNumber(recordings) && !hasDuplicateSequenceNumbers(recordings)) {
    return [];
  }

  const sorted = sortPinRecordingsBySequence(recordings);

  return sorted
    .map((recording, index) => ({ key: recording.key, sequenceNumber: index }))
    .filter((update, index) => sorted[index].sequenceNumber !== update.sequenceNumber);
}

/** Assign 0..n-1 sequence numbers for an explicit pin recording order (e.g. after reorder). */
export function buildPinRecordingOrderUpdates(
  orderedRecordings: SequenceSortable[]
): Array<{ key: string; sequenceNumber: number }> {
  return orderedRecordings.map((recording, sequenceNumber) => ({
    key: recording.key,
    sequenceNumber,
  }));
}

function getPinRecordings(
  pin: IPin,
  recordings: IRecording[],
  introRecordingKey?: string | null
): IRecording[] {
  return recordings.filter(
    (recording) =>
      recording.pinKey === pin.pinKey &&
      (!introRecordingKey || recording.key !== introRecordingKey)
  );
}

function pinLabel(pin: IPin): string {
  return pin.name?.trim() || pin.pinKey;
}

function albumLabel(pin: IPin): string {
  return pin.albumKey?.trim() || 'none';
}

/**
 * Checks pins with multiple recordings for missing or duplicate sequence numbers.
 * Matches what the mobile app uses for QR/album collection order.
 */
export function validatePinAlbumSequenceHealth(input: {
  pins: IPin[];
  recordings: IRecording[];
  introRecordingKey?: string | null;
}): PinAlbumSequenceHealthResult {
  const issues: PinAlbumSequenceHealthIssue[] = [];
  let affectedPinCount = 0;

  for (const pin of input.pins) {
    if (pin.pinType && PIN_TYPES_EXCLUDED_FROM_SEQUENCE_HEALTH.has(pin.pinType)) {
      continue;
    }

    const pinRecordings = getPinRecordings(pin, input.recordings, input.introRecordingKey);
    if (pinRecordings.length < 2) {
      continue;
    }

    const missingCount = pinRecordings.filter(
      (recording) =>
        recording.sequenceNumber === undefined ||
        recording.sequenceNumber === null ||
        !Number.isFinite(recording.sequenceNumber)
    ).length;

    const hasDuplicates = hasDuplicateSequenceNumbers(pinRecordings);
    if (missingCount === 0 && !hasDuplicates) {
      continue;
    }

    affectedPinCount += 1;

    if (missingCount > 0 && hasDuplicates) {
      issues.push({
        code: 'pin_sequence_numbers_invalid',
        severity: 'error',
        pinKey: pin.pinKey,
        message: `Pin "${pinLabel(pin)}" (album ${albumLabel(pin)}): ${missingCount} recording(s) missing sequence numbers and duplicate sequence values were found. The app may deliver tracks in the wrong order.`,
      });
      continue;
    }

    if (missingCount > 0) {
      issues.push({
        code: 'pin_missing_sequence_numbers',
        severity: 'error',
        pinKey: pin.pinKey,
        message: `Pin "${pinLabel(pin)}" (album ${albumLabel(pin)}): ${missingCount} recording(s) missing sequence numbers. The app will play unnumbered tracks last, which can skip earlier content.`,
      });
      continue;
    }

    issues.push({
      code: 'pin_duplicate_sequence_numbers',
      severity: 'error',
      pinKey: pin.pinKey,
      message: `Pin "${pinLabel(pin)}" (album ${albumLabel(pin)}): duplicate sequence numbers found. Collection order may be unpredictable in the app.`,
    });
  }

  return {
    isHealthy: issues.length === 0,
    issues,
    canAutoFix: issues.length > 0,
    affectedPinCount,
  };
}

/** Batch repair updates for all multi-recording pins in a project. */
export function buildPinAlbumSequenceRepairsForProject(input: {
  pins: IPin[];
  recordings: IRecording[];
  introRecordingKey?: string | null;
}): Array<{ key: string; sequenceNumber: number }> {
  const repairs: Array<{ key: string; sequenceNumber: number }> = [];

  for (const pin of input.pins) {
    if (pin.pinType && PIN_TYPES_EXCLUDED_FROM_SEQUENCE_HEALTH.has(pin.pinType)) {
      continue;
    }

    const pinRecordings = getPinRecordings(pin, input.recordings, input.introRecordingKey);
    if (pinRecordings.length < 2) {
      continue;
    }

    repairs.push(...getPinRecordingSequenceRepairs(pinRecordings));
  }

  return repairs;
}
